import { isMonetaryValueDataObject, MonetaryValue, MonetaryValueDataObjectType } from 'ComponentLibrary/src/common/money'
import { forwardRef } from 'react'
import { Control, FieldPathByValue, FieldPathValue, FieldValues, UseControllerProps, useController, useWatch } from 'react-hook-form'
import { NumberInputBase, NumberInputProps } from '../NumberInput/NumberInput'
import { isRequiredRule } from '../TextInput/TextInput'

export type MonetaryInputProps = Omit<NumberInputProps, 'defaultValue' | 'format' | 'onValueChange' | 'value'> & {
    defaultValue?: MonetaryValueDataObjectType
    onValueChange?: (value: MonetaryValue | undefined) => void
    value?: MonetaryValueDataObjectType
}

export const MonetaryInput = forwardRef<HTMLInputElement, MonetaryInputProps>(function MonetaryInput(props, ref) {
    const { defaultValue, onValueChange, unit = '€', value, ...numberInputProps } = props
    const isControlled = Object.prototype.hasOwnProperty.call(props, 'value')
    const numberValueProps = isControlled ? { value: toDecimal(value) } : {}

    return (
        <NumberInputBase
            {...numberInputProps}
            {...numberValueProps}
            defaultValue={toDecimal(defaultValue)}
            inputRef={ref}
            maximumInputFractionDigits={2}
            maximumFractionDigits={2}
            onValueChange={(nextValue) => {
                onValueChange?.(nextValue === undefined ? undefined : MonetaryValue.fromDecimal(nextValue))
            }}
            unit={unit}
        />
    )
})

type MonetaryFieldPath<TFieldValues extends FieldValues> = FieldPathByValue<TFieldValues, MonetaryValueDataObjectType | undefined>

type FormMonetaryInputRules<TFieldValues extends FieldValues, TName extends MonetaryFieldPath<TFieldValues>> = Omit<
    NonNullable<UseControllerProps<TFieldValues, TName>['rules']>,
    'max' | 'maxLength' | 'min' | 'minLength' | 'pattern' | 'valueAsNumber'
>

export type FormMonetaryInputProps<
    TFieldValues extends FieldValues,
    TName extends MonetaryFieldPath<TFieldValues> = MonetaryFieldPath<TFieldValues>,
> = Omit<MonetaryInputProps, 'defaultValue' | 'errorMessage' | 'name' | 'onValueChange' | 'required' | 'value'> & {
    control: Control<TFieldValues>
    name: TName
    rules?: FormMonetaryInputRules<TFieldValues, TName>
}

export function FormMonetaryInput<
    TFieldValues extends FieldValues,
    TName extends MonetaryFieldPath<TFieldValues> = MonetaryFieldPath<TFieldValues>,
>({ control, name, onBlur, rules, ...monetaryInputProps }: FormMonetaryInputProps<TFieldValues, TName>) {
    const { validate, ...otherRules } = rules ?? {}
    const { field, fieldState } = useController({
        control,
        name,
        rules: {
            ...otherRules,
            validate:
                validate ??
                ((fieldValue: FieldPathValue<TFieldValues, TName>) =>
                    fieldValue === undefined || isMonetaryValueDataObject(fieldValue) || 'Bitte geben Sie eine Zahl ein'),
        },
    })
    const value = useWatch({ control, exact: true, name })

    return (
        <MonetaryInput
            {...monetaryInputProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onValueChange={field.onChange}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={value}
        />
    )
}

function toDecimal(value: MonetaryValueDataObjectType | undefined) {
    return value === undefined ? undefined : value.amount / 100
}
